import React, { useEffect, useRef } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FilterIcon, ViewIcon } from 'components/common/icons';
import { usePurchaseRequest } from 'pages/admin/purchaseRequest/usePurchaseRequest';
import styles from 'pages/admin/clients/styles.module.scss';
import { Filters } from './filters';
import { HasAccess } from 'components/common/accessControl';
import { PERMISSIONS } from 'constants/permissions';
import { PurchaseRequestStatusBadge } from 'components/common/StatusBadge';
import { PurchaseRequestStatusType } from 'models/PurchaseRequest';
import { ADMIN_ROUTES } from 'constants/routes';
import { Button, Col, Form, FormInstance, Image, Input, PageTitle, Row, Table, Typography } from 'components';
import { Link } from 'react-router-dom';

const { Text } = Typography;
const { Search } = Input;

type EntityTableType = {
  name: string;
  id: string;
};

export interface PurchaseRequestDataType {
  id: string;
  uuid: string | number;
  brand: EntityTableType;
  client: EntityTableType;
  purchaseManager: EntityTableType;
  clientRequestNumber: string;
  status: PurchaseRequestStatusType;
  createDate: string;
  lastModifiedDate: string;
}

interface PurchaseRequestsProps {
  forDashboard?: boolean;
  onTotalUpdate?: (total: number) => void;
}

export const PurchaseRequests = React.memo(({ forDashboard, onTotalUpdate }: PurchaseRequestsProps) => {
  const [filtersForm] = Form.useForm<FormInstance>();
  const {
    onCloseFilters,
    onOpenFilters,
    openFilters,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    data,
    onSearch,
    total,
    applyFilters,
    resetFilters,
  } = usePurchaseRequest(forDashboard);

  // Track the previous total to avoid unnecessary updates
  const previousTotalRef = useRef<number | undefined>(undefined);

  // Report total count back to parent dashboard component
  useEffect(() => {
    if (forDashboard && onTotalUpdate && total !== undefined && previousTotalRef.current !== total) {
      // Update the ref to track the latest value
      previousTotalRef.current = total;
      onTotalUpdate(total);
    }
  }, [forDashboard, onTotalUpdate, total]);

  const columns: ColumnsType<PurchaseRequestDataType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: 130,
      ellipsis: true,
      render: (value) => {
        return (
          <Link to={`${ADMIN_ROUTES.brand.view}/${value?.id}/management`} target={'_blank'}>
            <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
              {value?.name}
            </Text>
          </Link>
        );
      },
    },
    {
      title: 'Client Name',
      dataIndex: 'client',
      key: 'client',
      width: 130,
      ellipsis: true,
      render: (value) => {
        return (
          <Link to={`${ADMIN_ROUTES.client}/${value?.id}/general`} target={'_blank'}>
            <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
              {value?.name}
            </Text>
          </Link>
        );
      },
    },
    {
      title: 'Client Request Number',
      dataIndex: 'clientRequestNumber',
      key: 'clientRequestNumber',
    },
    {
      title: 'Client Country',
      dataIndex: 'country',
      key: 'country',
      sorter: false,
      render: (country) => {
        return (
          <Row wrap={false} align={'middle'} className={'gap-12'}>
            {country?.fileLocation && (
              <Image
                src={country?.fileLocation}
                width={18}
                height={18}
                preview={false}
                style={{ verticalAlign: 'unset', width: '18px' }}
                className={'radius-circle'}
              />
            )}
            <Col>{country?.name}</Col>
          </Row>
        );
      },
    },
    {
      title: 'Purchase Manager',
      dataIndex: 'purchaseManager',
      key: 'purchaseManager',
      width: 200,
      render: (value) => {
        return (
          <Link to={`${ADMIN_ROUTES.users.view}/${value?.id}/account`} target={'_blank'}>
            <Text underline type={'secondary'} ellipsis={{ tooltip: true }}>
              {value?.firstName} {value?.lastName}
            </Text>
          </Link>
        );
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreatedDate',
      key: 'createDate',
      render: (value) => value?.day,
      minWidth: 160,
      sorter: true,
    },
    {
      title: 'Last Updated',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      render: (value) => value?.day,
      minWidth: 150,
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      minWidth: 100,
      sorter: true,
      render: (status) => <PurchaseRequestStatusBadge type={status} />,
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Button onClick={() => routeChange(value.id)} type={'text'} size={'small'} icon={<ViewIcon />} />
      ),
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Purchase Requests'} />
          </Row>
        </Col>
        <Col span={24} id={'purchase_request_list'} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.purchaseRequests.purchaseRequest.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="id"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  onChange={handleTableChange}
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    size: 'default',
                    current: pagination.page,
                    pageSize: pagination.size,
                    total: total,
                  }}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
    </Col>
  );
});
